// Copyright (c) Docugami, Inc. All rights reserved.

import { CoachMarkId } from "coachmarks/CoachMarkTypes";
import { ExplorerButtonId, ExplorerViewType, NavigationGroup } from "components/myContentExplorer/ExplorerTypes";
import { NavigationItemClassNames } from "components/myContentExplorer/NavigationItem";
import { NavigationListItem } from "components/myContentExplorer/NavigationItemList";
import { PlaceholderText } from "components/myContentExplorer/PlaceholderText";
import { useMemo } from "react";
import { AutomationId, getAutomationIdForDocSetId } from "utils/Automation";
import { dgIconNames } from "./dgIcons";
import { IExplorerNavigationGroup } from "./ExplorerNavigationGroups";
import { useNavigationService, NavigationType } from "./navigationService";
import { useAppSelector } from "./redux";
import { primaryTheme } from "components/shell/presentation/AppStyles";
import { createSelectOrgSampleClusters, useGetOrganizationSamplesQuery } from "store/clusters/samplesSlice";

const selectOrgSampleClusters = createSelectOrgSampleClusters();
export function useSamplesNavigationGroup(): IExplorerNavigationGroup {
    const sampleDocSets = useAppSelector(state => selectOrgSampleClusters(state));

    useGetOrganizationSamplesQuery();

    const navService = useNavigationService();

    const items = useMemo((): NavigationListItem[] => {
        const navItems: Array<NavigationListItem> = [];

        navItems.push(
            {
                id: ExplorerButtonId.BrowseSamples,
                title: 'BROWSE ALL',
                icon: {
                    name: dgIconNames.DocugamiLogo,
                    height: 14,
                    width: 11
                },
                hasDivider: true,
                type: ExplorerViewType.BrowseSamples,
                onClick: navService.navToBrowseSamplesPage,
                automationId: AutomationId.DocExplorerBrowseSamples,
                styles: [NavigationItemClassNames.navigationItemButton]
            }
        );

        sampleDocSets.forEach((docSet, idx) => {
            const badgeProps = !!docSet.sampleCatalogItemId ? undefined : ({
                badgeColor: primaryTheme.palette.neutralSecondaryAlt,
                badgeSize: {
                    outerBorderWidth: 1,
                    innerBorderWidth: 0,
                    spinnerBorderWidth: 0,
                    spinnerSize: 0,
                    badgeSize: 15
                },
                tooltip: 'Legacy Sample Doc Set'
            });

            navItems.push({
                id: docSet.clusterId!,
                title: docSet.label!,
                type: ExplorerViewType.DocSet,
                onClick: () => navService.navToDocSetPage(NavigationType.PushNewPage, docSet.clusterId!),
                automationId: getAutomationIdForDocSetId(docSet.clusterId!),
                hasDivider: idx === sampleDocSets.length - 1,
                badgeProps: badgeProps
            });
        })

        return navItems;
    }, [sampleDocSets, navService]);

    return {
        id: NavigationGroup.Samples,
        title: 'Samples',
        items: items,
        fallbackId: CoachMarkId.EmptyDocSets,
        placeholder: <PlaceholderText text="Browse all samples to add doc sets to your workspace and get started.." onClick={navService.navToBrowseSamplesPage} />,
        onExpand: items.length === 0 ? () => navService.navToEmptyContentCoachmark(NavigationType.PushNewPage) : undefined,
        statusItems: [],
        collapsedItemCount: 6
    }
}
